import React, { MutableRefObject, useCallback } from 'react';

import { isNil } from 'lodash-es';
import { useIntl } from 'react-intl';

import { ServiceMode } from '@rbi-ctg/menu';
import Dropdown from 'components/app-header/dropdown';
import { LeftNav } from 'components/app-header/left-nav';
import { RightNav } from 'components/app-header/right-nav';
import styleLoadingIndicator from 'components/app-header/style-loading-indicator';
import { AppSubheader } from 'components/app-subheader';
import CartPreview from 'components/cart-preview';
import { DeliveryBannerContainer } from 'components/delivery-banner';
import Logo from 'components/logo';
import { useFeatureNavigation } from 'hooks/use-feature-navigation';
import { useAccessibility } from 'state/accessibility';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoadingIndicator } from 'state/loading';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { useStoreContext } from 'state/store';
import { routes } from 'utils/routing';

import NavContainer from '../styled/nav-container';

import {
  Center,
  DesktopHeaderContainer,
  FlexContainer,
  LeftNavWrapper,
  MeasuredContainer,
  RightNavContainer,
  StyledLink,
} from './desktop-header.styled';
import LogoContainer from './styled/logo-container';

interface IDesktopHeaderProps {
  serviceMode: ServiceMode;
  userAddress: string;
  cateringPickupDateTime: string;
}

const DesktopHeader = React.forwardRef<HTMLDivElement, IDesktopHeaderProps>(
  ({ serviceMode, userAddress, cateringPickupDateTime }, ref: MutableRefObject<HTMLDivElement>) => {
    const { formatMessage } = useIntl();
    const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
    const enableBottomService = useFlag(LaunchDarklyFlag.ENABLE_BOTTOM_SERVICE_MODE);
    const enableBottomServiceMode = useFlag(LaunchDarklyFlag.ENABLE_BOTTOM_SERVICE_MODE);
    const mParticle = useMParticleContext();
    const { isStoreOpenAndAvailable, noStoreSelected } = useStoreContext();
    const LoadingIndicator = useLoadingIndicator();
    const StyledLoadingIndicator = styleLoadingIndicator(LoadingIndicator);
    const { featureAboutMenuEntriesDesktop: aboutMenuEntries } = useFeatureNavigation();
    const { isFullPageModalActive } = useAccessibility();

    // onReFocus Sets the focus of the clicked element to itself
    //
    // This is necessary due to the way that Reach Router changes the focus.
    // After the initial page load, reach router will move the focus to the outer most
    // component that is wrapped by the router. This component is outside of router,
    // so when clicking in the nav the focus is being pulled into the page.
    // For accessibility reasons, this is not the desired behavior.
    const onReFocus = useCallback(e => {
      e.persist();
      let target = e.target;
      window.setTimeout(() => {
        if (target && target.focus) {
          target.focus();
        }
      }, 200);
    }, []);

    const onLogoClick = useCallback(
      e => {
        mParticle.logNavigationClick(CustomEventNames.BUTTON_CLICK_LOGO);
        onReFocus(e);
      },
      [mParticle, onReFocus]
    );

    const onMyAccountClick = useCallback(
      e => {
        mParticle.logNavigationClick(CustomEventNames.BUTTON_CLICK_MY_ACCOUNT);
        onReFocus(e);
      },
      [mParticle, onReFocus]
    );

    // enable cart if a selected store is open and and available or no store is selected
    const enableCart =
      (isNil(enableOrdering) || enableOrdering) && (isStoreOpenAndAvailable || noStoreSelected);

    return (
      <DesktopHeaderContainer aria-hidden={isFullPageModalActive}>
        <MeasuredContainer ref={ref as any}>
          <NavContainer aria-label={formatMessage({ id: 'primaryNavLinks' })}>
            <FlexContainer>
              <LeftNavWrapper>
                <LeftNav />
                {aboutMenuEntries && (
                  <Dropdown
                    title={aboutMenuEntries?.name?.locale ?? ''}
                    entries={aboutMenuEntries?.pages ?? []}
                  />
                )}
              </LeftNavWrapper>
              <Center>
                <StyledLink to={routes.base} onClick={onLogoClick}>
                  <LogoContainer>
                    <Logo aria-hidden="true" />
                  </LogoContainer>
                </StyledLink>
              </Center>
              <RightNavContainer>
                <RightNav onMyAccountClick={onMyAccountClick} onReFocus={onReFocus} />
                {enableCart && <CartPreview isVisible={false} />}
              </RightNavContainer>
            </FlexContainer>
          </NavContainer>
          <StyledLoadingIndicator
            hasStoreSelection={isStoreOpenAndAvailable && !enableBottomService}
          />
          {!enableBottomServiceMode && <DeliveryBannerContainer />}
          <AppSubheader
            serviceMode={serviceMode}
            userAddress={userAddress}
            cateringPickupDateTime={cateringPickupDateTime}
          />
        </MeasuredContainer>
      </DesktopHeaderContainer>
    );
  }
);

export default DesktopHeader;
